/**
 * The "While you were away" modal (tech-design §2.4): after an offline catch-up the
 * player sees how long they were gone, what accrued, and anything that unlocked. Plain
 * DOM, no UI framework, to match the overlay. Pure presentation — the app computes the
 * catch-up (core) and hands this a ready view model.
 */
import { tagComponent } from './debugLabel.js';

export interface AwayResourceGain {
  readonly label: string;
  /** Pre-formatted amount (the app formats via `@core` so Decimals read nicely). */
  readonly amount: string;
}

export interface AwayModalViewModel {
  /** Elapsed absence actually simulated, in seconds. */
  readonly elapsedSec: number;
  /** True when the absence exceeded the cap (we note the time beyond the limit). */
  readonly capped: boolean;
  /** Positive resource gains, largest first (may be empty before real content lands). */
  readonly gains: readonly AwayResourceGain[];
  /** Notable unlocks (milestone titles/ids) crossed during the absence. */
  readonly unlocks: readonly string[];
  /** Logic ticks advanced — always meaningful, even with no resources yet. */
  readonly ticks: number;
}

/** Human-readable duration: "3d 4h", "2h 5m", "45s". */
export function formatDuration(totalSec: number): string {
  const sec = Math.max(0, Math.floor(totalSec));
  const days = Math.floor(sec / 86_400);
  const hours = Math.floor((sec % 86_400) / 3_600);
  const minutes = Math.floor((sec % 3_600) / 60);
  const seconds = sec % 60;
  const parts: string[] = [];
  if (days > 0) parts.push(`${String(days)}d`);
  if (hours > 0) parts.push(`${String(hours)}h`);
  if (minutes > 0) parts.push(`${String(minutes)}m`);
  if (parts.length === 0 || (days === 0 && hours === 0 && seconds > 0)) {
    parts.push(`${String(seconds)}s`);
  }
  return parts.join(' ');
}

/**
 * Mount a modal describing the absence and dismiss it on the close button, a backdrop
 * click, or Escape. Returns a `close()` handle for programmatic dismissal.
 */
export function showAwayModal(
  mount: HTMLElement,
  vm: AwayModalViewModel,
): { close: () => void } {
  const backdrop = document.createElement('div');
  backdrop.className = 'away-backdrop';
  backdrop.dataset.testid = 'away-modal';

  const dialog = document.createElement('div');
  dialog.className = 'away-dialog';
  dialog.setAttribute('role', 'dialog');
  dialog.setAttribute('aria-modal', 'true');
  dialog.setAttribute('aria-labelledby', 'away-title');
  tagComponent(dialog, 'away-dialog');

  const title = document.createElement('h2');
  title.id = 'away-title';
  title.textContent = 'While you were away';

  const elapsed = document.createElement('p');
  elapsed.className = 'away-elapsed';
  elapsed.dataset.testid = 'away-elapsed';
  elapsed.textContent = `You were gone ${formatDuration(vm.elapsedSec)}.`;
  if (vm.capped) {
    const capped = document.createElement('span');
    capped.className = 'away-capped';
    capped.textContent = ' (offline progress is capped)';
    elapsed.appendChild(capped);
  }

  const advanced = document.createElement('p');
  advanced.className = 'away-advanced';
  advanced.textContent = `The simulation advanced ${vm.ticks.toLocaleString()} ticks.`;

  dialog.append(title, elapsed, advanced);

  if (vm.gains.length > 0) {
    const heading = document.createElement('h3');
    heading.textContent = 'Resources gained';
    const list = document.createElement('ul');
    list.className = 'away-gains';
    for (const gain of vm.gains) {
      const item = document.createElement('li');
      const name = document.createElement('span');
      name.className = 'away-gain-label';
      name.textContent = gain.label;
      const value = document.createElement('span');
      value.className = 'away-gain-amount';
      value.textContent = `+${gain.amount}`;
      item.append(name, value);
      list.appendChild(item);
    }
    dialog.append(heading, list);
  }

  if (vm.unlocks.length > 0) {
    const heading = document.createElement('h3');
    heading.textContent = 'Unlocked while away';
    const list = document.createElement('ul');
    list.className = 'away-unlocks';
    for (const unlock of vm.unlocks) {
      const item = document.createElement('li');
      item.textContent = unlock;
      list.appendChild(item);
    }
    dialog.append(heading, list);
  }

  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'away-close';
  close.dataset.testid = 'away-close';
  close.textContent = 'Welcome back';
  dialog.appendChild(close);

  backdrop.appendChild(dialog);
  mount.appendChild(backdrop);

  const dismiss = (): void => {
    document.removeEventListener('keydown', onKey);
    backdrop.remove();
  };
  const onKey = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') dismiss();
  };

  close.addEventListener('click', dismiss);
  backdrop.addEventListener('click', (event) => {
    if (event.target === backdrop) dismiss();
  });
  document.addEventListener('keydown', onKey);
  close.focus();

  return { close: dismiss };
}
